import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Textarea } from "@/components/ui/textarea"
import { Message } from "@/modules/activities/lessons/components/message"
import { TypingIndicator } from "@/modules/activities/lessons/components/typing-indicator"
import { useMessages } from "@/modules/activities/lessons/hooks/use-messages"
import { usePreventNavigation } from "@/modules/activities/hooks/use-prevent-navigation"
import { useLessonsMessages } from "@/modules/activities/lessons/stores/use-lessons-message"
import { useLeftActivityAlertStore } from "@/modules/activities/store/use-left-activity-alert"
import { useAuth } from "@/modules/auth/store"
import { RefreshCw, Send, X } from "lucide-react"
import { useEffect, useRef } from "react"
import {
  type Location,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom"
import useInvalidateStudyPlannerQueries from "@/modules/activities/hooks/use-invalidate-queries"

export interface LessonIdPageState {
  name: string
  course: string
  id: string
}

export default function LessonPage() {
  const handleInvalidateQueries = useInvalidateStudyPlannerQueries()
  const { user } = useAuth()
  const navigate = useNavigate()
  const location: Location<LessonIdPageState> = useLocation()
  const { id: activityId } = useParams<{ id: string }>()
  const { onOpen } = useLeftActivityAlertStore()
  const { messages, displayButtons } = useLessonsMessages()
  const [searchParams, setSearchParams] = useSearchParams()
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const lessonName = location.state?.name || searchParams.get("name") || ""
  const lessonId = location.state?.id || searchParams.get("lessonId") || ""

  useEffect(() => {
    if (location.state) {
      setSearchParams(
        {
          name: location.state.name,
          course: location.state.course,
          lessonId: location.state.id,
        },
        { replace: true },
      )
    }
  }, [location.state, setSearchParams])

  const {
    inputMessage,
    isTyping,
    failedMessage,
    onHandleTextChange,
    handleSendMessage: originalHandleSendMessage,
    handleFinalLesson,
    handleRetry,
    handleContinueLearning,
  } = useMessages({
    id: lessonId,
    activityId: activityId ?? "",
  })

  // Custom send handler that resets textarea height
  const handleSendMessage = () => {
    originalHandleSendMessage()
    // Reset textarea height to default
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"
    }
  }

  const messagesEndRef = useRef<HTMLDivElement>(null)

  // Use the hook to prevent navigation via browser controls
  usePreventNavigation()

  // Scroll to bottom when messages change or typing indicator appears/disappears
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages, isTyping])

  if (!lessonId) {
    return <Navigate to="/activities/lessons/notFound" replace />
  }

  function handleBackButtonClick() {
    if (displayButtons) {
      handleInvalidateQueries()
      navigate(`/study`)
      return
    }
    onOpen()
  }

  return (
    <section className="z-5 absolute left-0 top-0 flex min-h-screen w-full flex-col gap-4 bg-white px-2 py-2 lg:flex-row lg:gap-8 lg:p-10">
      <section className="flex h-[90dvh] w-full flex-col gap-4 py-6">
        <Button variant="ghost" size="icon" className="self-end" onClick={handleBackButtonClick}>
          <X className="!size-6 text-neutral-600" />
        </Button>

        <section className="mx-auto flex h-full w-full max-w-5xl flex-col gap-4">
          <h1 className="text-2xl font-bold">{lessonName}</h1>

          <ScrollArea className="flex-1 flex-grow overflow-y-auto p-4">
            <div className="flex-grow overflow-y-auto">
              <div className="space-y-2">
                {messages.map((message) => (
                  <Message
                    key={message.id}
                    content={message.content}
                    type={message.type}
                    avatar={
                      message.type === "ai"
                        ? null
                        : {
                            href: user.profilePicUrl,
                            fallback: `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`,
                          }
                    }
                  />
                ))}

                {isTyping ? <TypingIndicator /> : null}
                {displayButtons ? (
                  <div className="flex gap-2 pl-12">
                    <Button variant="secondary" onClick={handleContinueLearning}>
                      Seguir aprendiendo
                    </Button>
                    <Button onClick={handleFinalLesson}>Finalizar lección</Button>
                  </div>
                ) : null}
                <div ref={messagesEndRef} />
              </div>
            </div>
          </ScrollArea>

          <section className="flex items-center gap-4 justify-self-end">
            <Textarea
              ref={textareaRef}
              value={inputMessage}
              onChange={(e) => onHandleTextChange(e.target.value)}
              placeholder="Escribe tu respuesta aquí..."
              className="max-h-16 resize-none overflow-y-auto"
              rows={3}
              disabled={displayButtons}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault()
                  handleSendMessage()
                }
              }}
            />
            {failedMessage ? (
              <Button
                size="icon"
                className="bg-yellow-500 text-white disabled:bg-neutral-100 disabled:text-neutral-400"
                onClick={handleRetry}
                title="Reintentar el envío del último mensaje"
              >
                <RefreshCw className="!size-6" />
              </Button>
            ) : (
              <Button
                size="icon"
                className="text-white disabled:bg-neutral-100 disabled:text-neutral-400"
                onClick={handleSendMessage}
                disabled={displayButtons}
              >
                <Send className="!size-6" />
              </Button>
            )}
          </section>
        </section>
      </section>
    </section>
  )
}
